import React, { useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Container, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem } from '@mui/material';
import { getOrders, placeOrder, cancelOrder } from '../services/vtradingApi';
import { useTranslation } from 'react-i18next';

const pinkTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#e91e63' },
    background: { default: '#fff0f6' },
  },
});

const orderTypes = [
  { value: 'market', label: 'Market' },
  { value: 'limit', label: 'Limit' }
];
const types = [
  { value: 'buy', label: 'Buy' },
  { value: 'sell', label: 'Sell' }
];

export default function VOrdersPage() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ coin_id: '', type: 'buy', order_type: 'market', price: '', amount: '' });
  const [placing, setPlacing] = useState(false);
  const [canceling, setCanceling] = useState(null);

  // TODO: Replace with real coin list from backend
  const coins = [
    { id: 1, symbol: 'USDT', name: 'Tether' },
    { id: 2, symbol: 'BTC', name: 'Bitcoin' },
    { id: 3, symbol: 'ETH', name: 'Ethereum' }
  ];

  const fetchOrders = () => {
    setLoading(true);
    getOrders()
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handlePlace = async () => {
    setPlacing(true);
    setError(null);
    try {
      await placeOrder({ ...form, price: form.order_type === 'market' ? undefined : form.price });
      setOpen(false);
      setForm({ coin_id: '', type: 'buy', order_type: 'market', price: '', amount: '' });
      fetchOrders();
    } catch (e) {
      setError(e.message);
    } finally {
      setPlacing(false);
    }
  };

  const handleCancel = async (orderId) => {
    setCanceling(orderId);
    setError(null);
    try {
      await cancelOrder(orderId);
      fetchOrders();
    } catch (e) {
      setError(e.message);
    } finally {
      setCanceling(null);
    }
  };

  return (
    <ThemeProvider theme={pinkTheme}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ bgcolor: 'background.default', p: 3, borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom color="primary">
            {t('vorders.title')}
          </Typography>
          <Button variant="contained" color="primary" onClick={handleOpen} sx={{ mb: 2 }}>
            {t('vorders.placeNewOrder')}
          </Button>
          {loading && <CircularProgress />}
          {error && <Alert severity="error">{error}</Alert>}
          {data && (
            <>
              <Typography variant="h6">{t('vorders.pendingOrders')}</Typography>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('vorders.coin')}</TableCell>
                      <TableCell>{t('vorders.type')}</TableCell>
                      <TableCell>{t('vorders.orderType')}</TableCell>
                      <TableCell align="right">{t('vorders.price')}</TableCell>
                      <TableCell align="right">{t('vorders.amount')}</TableCell>
                      <TableCell>{t('vorders.status')}</TableCell>
                      <TableCell>{t('vorders.created')}</TableCell>
                      <TableCell>{t('vorders.action')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.pending.map(o => (
                      <TableRow key={o.id}>
                        <TableCell>{o.symbol}</TableCell>
                        <TableCell>{o.type}</TableCell>
                        <TableCell>{o.order_type}</TableCell>
                        <TableCell align="right">{o.price}</TableCell>
                        <TableCell align="right">{o.amount}</TableCell>
                        <TableCell>{o.status}</TableCell>
                        <TableCell>{o.created_at}</TableCell>
                        <TableCell>
                          <Button variant="outlined" color="secondary" size="small" disabled={canceling === o.id} onClick={() => handleCancel(o.id)}>
                            {t('vorders.cancel')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="h6">{t('vorders.orderHistory')}</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('vorders.coin')}</TableCell>
                      <TableCell>{t('vorders.type')}</TableCell>
                      <TableCell>{t('vorders.orderType')}</TableCell>
                      <TableCell align="right">{t('vorders.price')}</TableCell>
                      <TableCell align="right">{t('vorders.amount')}</TableCell>
                      <TableCell>{t('vorders.status')}</TableCell>
                      <TableCell>{t('vorders.executed')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.history.map(o => (
                      <TableRow key={o.id}>
                        <TableCell>{o.symbol}</TableCell>
                        <TableCell>{o.type}</TableCell>
                        <TableCell>{o.order_type}</TableCell>
                        <TableCell align="right">{o.price}</TableCell>
                        <TableCell align="right">{o.amount}</TableCell>
                        <TableCell>{o.status}</TableCell>
                        <TableCell>{o.executed_at}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          <Dialog open={open} onClose={handleClose}>
            <DialogTitle>{t('vorders.placeNewOrder')}</DialogTitle>
            <DialogContent>
              <TextField
                select
                label={t('vorders.coin')}
                name="coin_id"
                value={form.coin_id}
                onChange={handleChange}
                fullWidth
                margin="normal"
              >
                {coins.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.symbol} - {c.name}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('vorders.type')}
                name="type"
                value={form.type}
                onChange={handleChange}
                fullWidth
                margin="normal"
              >
                {types.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t(`vorders.types.${t.value}`)}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('vorders.orderType')}
                name="order_type"
                value={form.order_type}
                onChange={handleChange}
                fullWidth
                margin="normal"
              >
                {orderTypes.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t(`vorders.orderTypes.${t.value}`)}</MenuItem>
                ))}
              </TextField>
              {form.order_type === 'limit' && (
                <TextField
                  label={t('vorders.limitPrice')}
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  type="number"
                  fullWidth
                  margin="normal"
                />
              )}
              <TextField
                label={t('vorders.amount')}
                name="amount"
                value={form.amount}
                onChange={handleChange}
                type="number"
                fullWidth
                margin="normal"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>{t('common.cancel')}</Button>
              <Button onClick={handlePlace} disabled={placing} variant="contained" color="primary">{t('vorders.placeOrder')}</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </ThemeProvider>
  );
} 